import { Breadcrumb, Modal, ProgressIndicator } from '@/components/atoms'
import { PageHeader, SaveCancelFooter } from '@/components/molecules'
import { DisplayContent, DisplayPreReqContent } from '@/containers'
import { getDisplayContentLanguagesService } from '@/services/languageService'
import { MediaCardUiSchema } from '@/services/schema/mediaCardJsonSchema'
import { useAppDispatch, useAppSelector } from '@/store'
import { setLanguages } from '@/store/slices/commonSlice'
import {
  resetSalesTarget,
  setSalesTargetDisplayContent,
  setSalesTargetDisplayContentMedia,
  setSalesTargetChildDisplayContent,
} from '@/store/slices/salesTargetSlice'
import { MODAL_TYPE } from '@/utils/constants/globalStyle'
import { ROUTES } from '@/utils/constants/routesConstants'
import { TARGET_TYPES } from '@/utils/constants/saleTargetConstants'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocation } from 'react-router-dom';

import { COUNTRY_LANGUAGES_CODES } from '@/utils/constants/globalConstants'
import { useSalesTargets } from '@/hooks/useSalesTargets'
import enUS from '@/internationalization/i18n/en-us'

export const AddSalesTargetDisplayContent = () => {
  const location = useLocation();
  const isEdit = location.state?.isEdit ?? false;

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const salesTarget = useAppSelector(state => state.salesTarget)
  const countryCode = useAppSelector(state => state.countryCode)?.countryCode
  const countryLanguages = COUNTRY_LANGUAGES_CODES[countryCode]
  const countryName = enUS?.COUNTRIES[countryCode] || ''
  const [isCancel, setIsCancel] = useState(false)
  const displayContentLanguages = useAppSelector(state => state.common.languages)
  const [activeLanguage, setActiveLanguage] = useState('en')
  const { handleDraft } = useSalesTargets()
  const [displayContentFormData, setDisplayContentFormData] = useState(() => {
    const languages = COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || []
    const formData = {}
  
    languages.forEach(lang => {
      const code = lang?.code
      formData[code] = {
        targetType: salesTarget?.targetType,
        ...salesTarget?.displayContent?.[code],
      }
    })
    return formData
  })

  const [displayPreReqContentFormData, setDisplayPreReqContentFormData] = useState(() => {
    const languages = COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || []
    const parentData = {}
    const childData = {}

    languages.forEach(lang => {
      const code = lang.code
      parentData[code] = {
        targetType: salesTarget?.targetType,
        ...salesTarget?.displayContent?.[code],
      }
      childData[code] = {
        targetType: salesTarget?.targetType,
        ...salesTarget?.child?.displayContent?.[code],
      }
    })

    return {
      ...parentData,
      child: childData,
    }
  })

  const [mediaCardFormData, setMediaCardFormData] = useState({
    title: salesTarget?.displayContentMedia?.title ?? salesTarget?.targetType,
    imageId: salesTarget?.displayContentMedia?.imageId ?? '',
    imageUri: salesTarget?.displayContentMedia?.imageUri ?? '',
    imageBase64: salesTarget?.displayContentMedia?.imageBase64 ?? '',
    optIn: salesTarget?.displayContentMedia?.optIn ?? '',
    imageUrl: salesTarget?.displayContentMedia?.imageUrl,
  })

  const isPrerequisiteTarget = salesTarget.targetType === TARGET_TYPES.MonthlyTargetPrerequistite || salesTarget.targetType === TARGET_TYPES.ProductTargetPrerequisite

  const handleNextAction = () => {
    if (isPrerequisiteTarget) {
      dispatch(setSalesTargetDisplayContent(displayPreReqContentFormData))
      dispatch(setSalesTargetChildDisplayContent(displayPreReqContentFormData.child))
      dispatch(setSalesTargetDisplayContentMedia({
        title: salesTarget?.displayContentMedia?.title,
        imageId: salesTarget?.displayContentMedia?.imageId,
        imageUri: salesTarget?.displayContentMedia?.imageUri,
        imageBase64: salesTarget?.displayContentMedia?.imageBase64,
        optIn: salesTarget?.displayContentMedia?.optIn,
        imageUrl: salesTarget?.displayContentMedia?.imageUrl,
      }))
      navigate(ROUTES.SALES_TARGET_PRODUCTS, { state: { isEdit: isEdit } });
    } else {
      dispatch(setSalesTargetDisplayContent(displayContentFormData))
      dispatch(setSalesTargetDisplayContentMedia({
        title: salesTarget?.displayContentMedia?.title,
        imageId: salesTarget?.displayContentMedia?.imageId,
        imageUri: salesTarget?.displayContentMedia?.imageUri,
        imageBase64: salesTarget?.displayContentMedia?.imageBase64,
        optIn: salesTarget?.displayContentMedia?.optIn,
        imageUrl: salesTarget?.displayContentMedia?.imageUrl,
      }))
      navigate(ROUTES.SALES_TARGET_PRODUCTS, { state: { isEdit: isEdit } });
    }
  }

  const handleDraftAction = async () => {
    const response = await handleDraft(
      salesTarget,
      salesTarget,
      isPrerequisiteTarget,
      'displayContent',
      {
        displayContentFormData,
        displayPreReqContentFormData
      }
    )
    
    if (response?.success) {
      navigate(ROUTES.SALES_TARGETS)
    }
  }

  const handleCancel = () => {
    setIsCancel(!isCancel)
  }

  const onClickPrimaryButton = () => {
    dispatch(resetSalesTarget())
    navigate(ROUTES.SALES_TARGETS)
  }

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }

  const getDisplayContentLanguages = () => {
    getDisplayContentLanguagesService().then(res => {
      dispatch(setLanguages(res))
      setActiveLanguage(res[0].code)
    })
  }

  useEffect(() => {
    setMediaCardFormData({
      title: salesTarget?.displayContentMedia?.title,
      imageId: salesTarget?.displayContentMedia?.imageId,
      imageUri: salesTarget?.displayContentMedia?.imageUri,
      imageBase64: salesTarget?.displayContentMedia?.imageBase64,
      optIn: salesTarget?.displayContentMedia?.optIn,
      imageUrl: salesTarget?.displayContentMedia?.imageUrl,
    })
  }, [salesTarget?.displayContentMedia])

  useEffect(() => {
    if (countryLanguages && countryLanguages?.length > 0) {
      dispatch(setLanguages(countryLanguages))
      setActiveLanguage(countryLanguages[0].code)
    } else {
      getDisplayContentLanguages()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryLanguages])
  useEffect(() => {
    if (!salesTarget.targetType) navigate(ROUTES.SALES_TARGET_ADD)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    const languages = COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || []
    if (languages && languages?.length > 0) {
      const initialFormData = languages.reduce((acc, lang) => {
        acc[lang.code] = {
          targetType: salesTarget?.targetType,
          ...salesTarget?.displayContent?.[lang.code],
        }
        return acc
      }, {})
      setDisplayContentFormData(initialFormData)
    }
  }, [displayContentLanguages, salesTarget?.targetType, salesTarget?.displayContent, countryLanguages,countryCode])

  useEffect(() => {
    const languages = COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || []
    if (!languages || languages.length === 0) return
      const updatedParent = {}
      const updatedChild = {}
      languages.forEach(lang => {
        const code = lang.code
        updatedParent[code] = {
          targetType: salesTarget?.targetType,
          ...(salesTarget?.displayContent?.[code] || {}),
        }
        updatedChild[code] = {
          targetType: salesTarget?.targetType,
          ...(salesTarget?.child?.displayContent?.[code] || {}),
        }
      })
      setDisplayPreReqContentFormData({
        ...updatedParent,
        child: updatedChild,
      })
  }, [displayContentLanguages, salesTarget?.targetType, salesTarget?.displayContent, salesTarget?.child?.displayContent, countryLanguages, countryCode])


  const handleTabClick = langCode => {
    setActiveLanguage(langCode)
    if (!displayContentFormData[langCode]) {
      const newData = {
        targetType: salesTarget?.targetType,
        title: salesTarget?.displayContent?.[langCode]?.title || null,
        category: salesTarget?.displayContent?.[langCode]?.category || '',
        parentCategory: salesTarget?.displayContent?.[langCode]?.parentCategory || '',
        childCategory: salesTarget?.displayContent?.[langCode]?.childCategory || '',
        excerpt: salesTarget?.displayContent?.[langCode]?.excerpt || '',
        terms: salesTarget?.displayContent?.[langCode]?.terms || '',
      }

      setDisplayContentFormData(prevData => ({
        ...prevData,
        [langCode]: newData,
      }))
    }
  }

  const handleTabClickPreReq = (tab) => {
    const languages = COUNTRY_LANGUAGES_CODES[countryCode] || displayContentLanguages || []
    const updateLanguages = (existing = {}) => {
      const updated = { ...existing }
      languages.forEach(({ code }) => {
        if (!updated[code]) {
          updated[code] = {
            targetType: salesTarget?.targetType,
            title: '',
            excerpt: '',
            terms: '',
            category: '',
            ...(tab === 'parent' && { parentCategory: '', childCategory: '' }),
          }
        }
      })
      return updated
    }

    setDisplayPreReqContentFormData(prevData => (
      tab === 'parent'
        ? updateLanguages(prevData)
        : { ...prevData, child: updateLanguages(prevData.child) }
    ))
  }

  const isLanguageContentValid = content => {
    if (!content) return false
    const validationFields = ['excerptValid', 'termsValid', 'titleValid', 'title', 'excerpt', 'terms']
    return validationFields.some(
      field => content[field] === true || content[field] === undefined || content[field] === ''
    )
  }

  const validateDisplayContent = displayContentFormData => {
    return Object.values(displayContentFormData).some(languageContent => isLanguageContentValid(languageContent))
  }

  const getFormError = () => {
    let isInValid = true
    const { displayContentMedia } = salesTarget
    if (displayContentMedia?.imageUrl == '' || displayContentMedia?.imageUrl == null || displayContentMedia?.length === 0) {
      return true
    }
    if (isPrerequisiteTarget) {
      const parentForm = Object.fromEntries(
        Object.entries(displayPreReqContentFormData).filter(([key]) => key !== 'child')
      )
      const isParentInValid = validateDisplayContent(parentForm)
      const isChildInValid = validateDisplayContent(displayPreReqContentFormData.child)
      isInValid = isParentInValid || isChildInValid
    } else {
      isInValid = validateDisplayContent(displayContentFormData)
    }
    return isInValid
  }

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: '/',
              label: '',
            },
            {
              href: '',
              label: `Add Sales Target ${countryName}`,
            },
          ],
        }}
        id={'astdc_bradcrumb'}
      />

      <PageHeader title={'Add Sales Target'} />

      <ProgressIndicator
        currentStep={2}
        completed={false}
        orientation="horizontal"
        steps={4}
        titles={[{ title: 'Setup' }, { title: 'Display Content' }, { title: 'Products' }, { title: 'Review' }]}
        className="mb-6"
      />
      <br />
      {isPrerequisiteTarget ? (
        <DisplayPreReqContent
          MediaCardUiSchema={MediaCardUiSchema}
          setDisplayPreReqContentFormData={setDisplayPreReqContentFormData}
          displayPreReqContentFormData={displayPreReqContentFormData}
          mediaCardFormData={mediaCardFormData}
          setMediaCardFormData={setMediaCardFormData}
          handleTabClickPreReq={handleTabClickPreReq}
          languages={countryLanguages && countryLanguages?.length > 0 ? countryLanguages : displayContentLanguages}
          targetType={salesTarget?.targetType} />
      ) : (
        <DisplayContent
          MediaCardUiSchema={MediaCardUiSchema}
          setDisplayContentFormData={setDisplayContentFormData}
          displayContentFormData={displayContentFormData}
          mediaCardFormData={mediaCardFormData}
          setMediaCardFormData={setMediaCardFormData}
          languages={countryLanguages && countryLanguages?.length > 0 ? countryLanguages : displayContentLanguages}
          handleTabClick={handleTabClick}
          activeLanguage={activeLanguage}
          targetType={salesTarget.targetType}
          joinCampaignFlag={salesTarget?.joinCampaignFlag}
        />
      )}
      {isCancel ? (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title="Are you sure you want to cancel?"
          text={'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'}
          showPrimaryButton={true}
          primaryButtonText="Yes"
          onClickPrimaryButton={onClickPrimaryButton}
          showSecondaryButton={true}
          secondaryButtonText="No"
          onClickSecondaryButton={onClickSecondaryButton}
          footerProps={{
            size: 'medium',
            type: 'primary',
          }}
          isDark={true}
        />
      ) : null}

      <SaveCancelFooter
        FormError={getFormError()}
        handleCancel={handleCancel}
        draftAction={handleDraftAction}
        nextAction={handleNextAction}
      />
    </>
  )
}
